import {
    AfterViewChecked,
    ChangeDetectorRef,
    Component,
    Input,
    OnInit,
    ViewChild
} from '@angular/core';
import { WizardModalComponent } from "@app/shared/components";
import { ToolFormModel } from "../../tool-form-modal/tool-create.model";
import { ToolDetailsFormComponent } from "@app/tool/tool-form-modal/forms";
import { ToolService } from "@services/tool.service";
import { Log } from "ng2-logger";
import { ToastsManager } from "@app/shared/util/toastr.service";
import { TranslateService } from "@ngx-translate/core";
import { AbstractControl, UntypedFormControl, UntypedFormGroup } from "@angular/forms";
import { of } from 'rxjs';
import { debounceTime, switchMap, tap } from 'rxjs/operators';
import { ToolProperty } from '@app/tool/model/enums/tool-property.enum';
import {
    CODING_TYPE_NA_OPTION,
    CODING_TYPE_OPTIONS,
    CodingTypeOption,
    CodingTypeValue,
    isAutoGeneratedToolCodeCodingType,
    isNACodingType,
    normalizeCodingTypeValue
} from '@app/shared/models/enums/codingType.enum';
import { ToolPropertyValidationState } from '@app/tool/model/tool-property-validation-state.enum';
import { ToolValidationConflictTool } from '@app/tool/model/tool-validation-conflict-tool.model';
import { ToolPropertyValidationResult } from '@app/tool/model/tool-property-validation-result.model';
import { ToolPropertyValidationInfoModalComponent } from '@app/tool/components/tool-property-validation-info-modal/tool-property-validation-info-modal.component';
import { merge } from 'rxjs';


@Component({
    selector: 'app-add-tool-in-set-modal',
    templateUrl: './add-tool-in-set-modal.component.html',
    styleUrls: ['./add-tool-in-set-modal.component.scss']
})
export class AddToolInSetModalComponent implements OnInit, AfterViewChecked {
    _logger = Log.create(AddToolInSetModalComponent.name);

    @ViewChild(WizardModalComponent, {static: true}) wizardModal: WizardModalComponent;
    @ViewChild(ToolDetailsFormComponent, {static: true}) toolDetailsForm: ToolDetailsFormComponent;
    @ViewChild('infoModal', { static: true }) infoModal: ToolPropertyValidationInfoModalComponent;

    @Input() tool = new ToolFormModel();

    toolDatamatrix = [];
    selectedStep: number;
    toolSet;
    isLoading: boolean;
    showValidations = false;

    isSpareForm: UntypedFormGroup;
    detailsForm: UntypedFormGroup;

    codingTypeOptions: CodingTypeOption[] = CODING_TYPE_OPTIONS;
    infoModalTools: ToolValidationConflictTool[] = [];
    private acknowledgedInfoSignature: string = null;
    private pendingInfoContext: { signature: string } = null;

    constructor(private toolService: ToolService,
                private toastr: ToastsManager,
                private translateService: TranslateService,
                private cdr: ChangeDetectorRef) {
    }

    ngOnInit() {
        this.initIsSpareForm();
        this.initDetailsForm();
    }

    ngAfterViewChecked(): void {
        this.cdr.detectChanges();
    }

    initIsSpareForm() {
        this.isSpareForm = new UntypedFormGroup({
            isSpare: new UntypedFormControl(false)
        })
    }

    initDetailsForm() {
        const selectedCodingType = this.getCodingTypeOption(normalizeCodingTypeValue(this.tool.codingTypes[0]));

        this.detailsForm = new UntypedFormGroup({
            mesCode: new UntypedFormControl(
                isNACodingType(selectedCodingType.value)
                    ? 'N/A'
                    : isAutoGeneratedToolCodeCodingType(selectedCodingType.value)
                        ? ''
                        : this.tool.mesCodes[0]
            ),
            supplierCode: new UntypedFormControl(this.tool.supplierCodes[0]),
            inventoryNumber: new UntypedFormControl(this.tool.inventoryNumbers[0]),
            toolControlName: new UntypedFormControl(this.tool.toolControlNames[0]),
            codingType: new UntypedFormControl(selectedCodingType)
        });

        merge(
            this.detailsForm.get('mesCode')!.valueChanges.pipe(
                debounceTime(500)
            ),
            this.detailsForm.get('codingType')!.valueChanges
        ).pipe(
            tap(() => this.resetInfoAcknowledgement()),
            switchMap(() => this.validateToolCode())
        ).subscribe(result => {
            this.applyServerValidationResult(result);
        });

        this.detailsForm.get('codingType')!.valueChanges.subscribe(() => {
            this.syncToolCodeWithCodingType();
        });
    }

    onWizardCancel() {
        this.reset();
        this.toolService.reloadTools();
        this.wizardModal.close();
    }

    open(toolSet) {
        this.toolSet = toolSet;
        this.wizardModal.open();
    }

    close() {
        this.reset();
        this.wizardModal.close();
    }

    reset() {
        this.tool = new ToolFormModel();
        if (this.isSpareForm) {
            this.isSpareForm.reset({
                isSpare: false
            });
        }

        if (this.detailsForm) {
            this.detailsForm.reset({
                mesCode: null,
                supplierCode: null,
                inventoryNumber: null,
                toolControlName: null,
                codingType: CODING_TYPE_NA_OPTION
            });
            this.syncToolCodeWithCodingType();
        }

        this.acknowledgedInfoSignature = null;
        this.pendingInfoContext = null;
        this.infoModalTools = [];
        this.toolDatamatrix = [];
        this.showValidations = false;
        this.isLoading = false;

        this.wizardModal.reset();
    }

    onStepChange(stepIndex) {
        this.selectedStep = stepIndex;
    }


    onWizardSubmit() {
        const toolUID = this.toolSet.children[0].toolUid;

        const formValue = this.detailsForm.value;
        const codingType = formValue.codingType?.value ?? formValue.codingType ?? 'N/A';

        const toolData = {
            isSpare: this.isSpareForm.value.isSpare,
            mesCode: this.getSubmittedToolCode(),
            supplierCode: this.detailsForm.value.supplierCode,
            inventoryNumber: this.detailsForm.value.inventoryNumber,
            toolControlName: this.detailsForm.value.toolControlName,
            codingType
        };

        this.isLoading = true;
        this.toolService.addToolIntoSet(toolUID, toolData).subscribe(
            (data) => {
                this.isLoading = false;
                this._logger.info("addToolIntoSet:: API returned data ", data);
                this.toolService.reloadTools();
                const lastToolIndex = data[0].sets[0].tools;
                const createdTool = lastToolIndex[lastToolIndex.length - 1];
                this.toastr.success(this.buildCreateMessage(createdTool.name));
                this.toolDatamatrix = [];
                this.getToolDataMatrix(createdTool);
                this.wizardModal.next();
            },
            (error) => {
                this.isLoading = false;
                this._logger.info("addToolIntoSet:: API returned error ", error);
            });
    }

    buildCreateMessage(toolName) {
        return this.translateService.instant('admin.tools.toasts.created', {toolName: toolName});
    }

    get canSave(): boolean {
        return this.detailsForm.valid && !this.detailsForm.pending;
    }

    canReopenInfoModal(): boolean {
        return !!this.detailsForm.get('mesCode')?.errors?.toolCodeInfoPending && !!this.infoModalTools.length;
    }

    isToolCodeReadonly(): boolean {
        return isNACodingType(this.getCodingTypeValue()) || this.isToolCodeAutoGenerated();
    }

    isToolCodeAutoGenerated(): boolean {
        return isAutoGeneratedToolCodeCodingType(this.getCodingTypeValue());
    }

    reopenInfoModal() {
        if (!this.canReopenInfoModal()) {
            return;
        }

        this.pendingInfoContext = {
            signature: this.buildValidationSignature()
        };
        this.infoModal.open();
    }

    onInfoConfirm() {
        if (!this.pendingInfoContext) {
            return;
        }

        this.acknowledgedInfoSignature = this.pendingInfoContext.signature;
        this.pendingInfoContext = null;
        this.removeError(this.detailsForm.get('mesCode')!, 'toolCodeInfoPending');
        this.infoModalTools = [];
    }

    private validateToolCode() {
        const mesCodeCtrl = this.detailsForm.get('mesCode')!;
        const trimmedCode = this.normalizeCode(mesCodeCtrl.value);
        const codingType = this.getCodingTypeValue();
        const equipmentIds = this.getRelatedEquipmentIds();

        this.removeError(mesCodeCtrl, 'toolCodeNotUnique');
        this.removeError(mesCodeCtrl, 'toolCodeInfoPending');

        if (!trimmedCode || isNACodingType(codingType) || isAutoGeneratedToolCodeCodingType(codingType) || !equipmentIds.length) {
            return of(null);
        }

        return this.toolService.validateToolProperty(ToolProperty.MESCode, trimmedCode, {
            codingType,
            equipmentIds
        });
    }

    private applyServerValidationResult(result: ToolPropertyValidationResult | null) {
        const mesCodeCtrl = this.detailsForm.get('mesCode')!;
        const signature = this.buildValidationSignature();

        if (result === null) {
            return;
        }

        switch (result.validationState) {
            case ToolPropertyValidationState.Valid:
                this.removeError(mesCodeCtrl, 'toolCodeNotUnique');
                this.removeError(mesCodeCtrl, 'toolCodeInfoPending');
                return;
            case ToolPropertyValidationState.Info:
                if (this.acknowledgedInfoSignature === signature) {
                    this.removeError(mesCodeCtrl, 'toolCodeInfoPending');
                    return;
                }

                this.setError(mesCodeCtrl, 'toolCodeInfoPending');
                this.infoModalTools = result.tools || [];
                this.pendingInfoContext = { signature };
                this.infoModal.open();
                return;
            case ToolPropertyValidationState.Error:
                this.removeError(mesCodeCtrl, 'toolCodeInfoPending');
                this.setError(mesCodeCtrl, 'toolCodeNotUnique', {
                    tools: this.buildToolsLabel(result.tools || [])
                });
                return;
            default:
                this.removeError(mesCodeCtrl, 'toolCodeNotUnique');
        }
    }

    private getRelatedEquipmentIds(): number[] {
        return this.toolSet?.children?.[0]?.details?.equipment
            ?.map(equipment => equipment?.id)
            .filter(id => !!id) || [];
    }

    private getCodingTypeValue(): CodingTypeValue {
        const rawValue = this.detailsForm.get('codingType')?.value?.value
            ?? this.detailsForm.get('codingType')?.value
            ?? 'N/A';
        return normalizeCodingTypeValue(rawValue);
    }

    private getCodingTypeOption(value: CodingTypeValue): CodingTypeOption {
        return this.codingTypeOptions.find(opt => opt.value === value) || CODING_TYPE_NA_OPTION;
    }

    private normalizeCode(value: string): string {
        return value?.trim() || '';
    }

    private syncToolCodeWithCodingType() {
        const mesCodeCtrl = this.detailsForm.get('mesCode')!;
        const codingType = this.getCodingTypeValue();
        const isNA = isNACodingType(codingType);
        const isAutoGenerated = isAutoGeneratedToolCodeCodingType(codingType);

        if (isNA) {
            mesCodeCtrl.setValue('N/A', { emitEvent: false });
            this.removeError(mesCodeCtrl, 'toolCodeNotUnique');
            this.removeError(mesCodeCtrl, 'toolCodeInfoPending');
            return;
        }

        if (isAutoGenerated) {
            mesCodeCtrl.setValue('', { emitEvent: false });
            this.removeError(mesCodeCtrl, 'toolCodeNotUnique');
            this.removeError(mesCodeCtrl, 'toolCodeInfoPending');
            return;
        }

        if (this.normalizeCode(mesCodeCtrl.value) === 'N/A') {
            mesCodeCtrl.setValue('', { emitEvent: false });
        }
    }

    private getSubmittedToolCode(): string | null {
        return this.isToolCodeAutoGenerated() ? null : this.detailsForm.value.mesCode;
    }

    private buildValidationSignature(): string {
        return JSON.stringify({
            mesCode: this.normalizeCode(this.detailsForm.get('mesCode')?.value),
            codingType: this.getCodingTypeValue(),
            equipmentIds: this.getRelatedEquipmentIds()
        });
    }

    private buildToolsLabel(tools: ToolValidationConflictTool[]): string {
        return tools
            .map(tool => `${tool.toolUid} - ${tool.name}`)
            .join(', ');
    }

    private resetInfoAcknowledgement() {
        this.acknowledgedInfoSignature = null;
        this.removeError(this.detailsForm.get('mesCode')!, 'toolCodeInfoPending');
        this.pendingInfoContext = null;
        this.infoModalTools = [];
    }

    private setError(control: AbstractControl, key: string, value: any = true) {
        control.setErrors({
            ...(control.errors || {}),
            [key]: value
        });
    }

    private removeError(control: AbstractControl, key: string) {
        const errors = { ...(control.errors || {}) };
        delete errors[key];
        control.setErrors(Object.keys(errors).length ? errors : null);
    }

    private getToolDataMatrix(tool) {
        this._logger.info('getToolDataMatrix:: Retrieving tool datamatrixes', tool);
        this.toolService.getToolDatamatrix(tool.id).subscribe(toolDM => {
            this._logger.info('getToolDataMatrix:: API returned data', toolDM);
            this.toolDatamatrix.push(toolDM[toolDM.length - 1]);
        });
    }

}
